import {
  collection,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  getDocs,
  query,
  where,
  orderBy,
  Timestamp,
} from 'firebase/firestore';
import { db } from '../firebase';
import { TimeEntry } from '../../types/worksheet';

const COLLECTION = 'worksheetTimeEntries';

export const timeTrackingService = {
  async startTimer(worksheetId: string, userId: string, userName: string, taskId?: string): Promise<string> {
    const entry: Record<string, any> = {
      worksheetId,
      userId,
      userName,
      startTime: Timestamp.fromDate(new Date()),
      duration: 0,
      isManual: false,
      createdAt: Timestamp.fromDate(new Date()) as any,
    };
    if (taskId) entry.taskId = taskId;
    const docRef = await addDoc(collection(db, COLLECTION), entry);
    return docRef.id;
  },

  async stopTimer(entryId: string): Promise<void> {
    const now = new Date();
    const entryRef = doc(db, COLLECTION, entryId);
    const snapshot = await getDocs(query(collection(db, COLLECTION), where('__name__', '==', entryId)));
    if (snapshot.empty) return;
    const data = snapshot.docs[0].data();
    const startTime = data.startTime?.toDate() || new Date();
    const duration = Math.round((now.getTime() - startTime.getTime()) / 60000);
    await updateDoc(entryRef, {
      endTime: Timestamp.fromDate(now),
      duration,
    });
  },

  async addManualEntry(
    worksheetId: string,
    userId: string,
    userName: string,
    startTime: Date,
    endTime: Date,
    description?: string,
    workerCount?: number,
    taskId?: string
  ): Promise<string> {
    const duration = Math.round((endTime.getTime() - startTime.getTime()) / 60000);
    const entry: Record<string, any> = {
      worksheetId,
      userId,
      userName,
      startTime: Timestamp.fromDate(startTime),
      endTime: Timestamp.fromDate(endTime),
      duration,
      workerCount: workerCount || 1,
      isManual: true,
      createdAt: Timestamp.fromDate(new Date()) as any,
    };
    if (taskId) entry.taskId = taskId;
    if (description) entry.description = description;
    const docRef = await addDoc(collection(db, COLLECTION), entry);
    return docRef.id;
  },

  async deleteEntry(entryId: string): Promise<void> {
    await deleteDoc(doc(db, COLLECTION, entryId));
  },

  async getByWorksheet(worksheetId: string): Promise<TimeEntry[]> {
    const q = query(
      collection(db, COLLECTION),
      where('worksheetId', '==', worksheetId),
      orderBy('startTime', 'desc')
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map(d => ({
      id: d.id,
      ...d.data(),
      startTime: d.data().startTime?.toDate() || new Date(),
      endTime: d.data().endTime?.toDate() || undefined,
      createdAt: d.data().createdAt?.toDate() || new Date(),
    })) as TimeEntry[];
  },

  async getTotalMinutes(worksheetId: string): Promise<number> {
    const entries = await this.getByWorksheet(worksheetId);
    return entries.reduce((sum, e) => sum + (e.duration || 0) * (e.workerCount || 1), 0);
  },
};
